<script lang="ts">
	//Stores
	import { auth_locked_devices, pin_unlocked_devices } from 'src/stores';
	import { devices } from 'src/stores';
	import { os } from 'src/stores';
	//Services
	import { format } from 'svelte-i18n';
	import { zwaveMultilevelSwitchDirection } from '../../../services/xz_http_reqs';
	import { hapticFeedback } from '../../../services/native_container_requests_ios';
	//Helpers
	import {
		get_z_wave_channel_structure_from_category,
		real_id,
	} from '../../../helpers/interface_helpers';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';
	import { hasControlRights } from 'src/helpers/user_helpers';
	//Design system components
	import {
		DeviceButton,
		DeviceButtonV2,
		DoubleDeviceButton,
		DoubleDeviceButtonV2,
		toast,
		type DeviceButtonV2PropsTypes,
		type DoubleDeviceButtonV2PropsTypes,
	} from '@Shelly/shelly-design-system';
	//Components
	import PinUnlock from '../../PinUnlock.svelte';

	let className = '';
	export { className as class };

	export let device, status;
	export let devBtnsV2Props: {
		deviceButtonV2?: Partial<DeviceButtonV2PropsTypes>;
		doubleDeviceButtonV2?: Partial<DoubleDeviceButtonV2PropsTypes>;
	} = {
		deviceButtonV2: { size: 's' },
		doubleDeviceButtonV2: { size: 's' },
	};
	export let useV2Components = false;

	let isLoading = false;
	let direction: 'opening' | 'closing' | 'stopped' | undefined;
	let pinLocked;
	let showModal = false;
	let callbackValue;

	$: hasRights = hasControlRights(device);
	$: component = get_z_wave_channel_structure_from_category(device);
	$: coverStatus = status?.[component];
	$: coverState = coverStatus?.state;
	$: btn1 = coverState === 'opening' || direction === 'opening';
	$: btn2 = coverState === 'closing' || direction === 'closing';
	$: pinLocked =
		typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;

	$: if (coverState === 'stopped' || coverState === 'open' || coverState === 'closed') {
		direction = undefined;
	}

	const handleDirectionRequest = async (dir: 0 | 1) => {
		isLoading = true;

		const isAlreadyMovingSameWay =
			(dir === 1 && (coverState === 'opening' || direction === 'opening')) ||
			(dir === 0 && (coverState === 'closing' || direction === 'closing'));

		const nextDirection = isAlreadyMovingSameWay ? 'stop' : dir === 1 ? 'open' : 'close';
		direction =
			nextDirection === 'open' ? 'opening' : nextDirection === 'close' ? 'closing' : 'stopped';

		const res = await zwaveMultilevelSwitchDirection(device, nextDirection);

		if (!res.isok && $auth_locked_devices.indexOf(real_id(device.id)) === -1) {
			direction = undefined;
			toast.open({ type: 'danger', title: $format('error_global') });
		}

		setTimeout(() => {
			isLoading = false;
		}, 100);
	};

	const handleRequest = (dir: 0 | 1) => {
		if (!pinLocked) {
			if ($os === 'IOS') {
				hapticFeedback('buttonPressed');
			}
			handleDirectionRequest(dir);
		} else {
			callbackValue = dir;
			showModal = true;
		}
	};

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		handleRequest(callbackValue);
		callbackValue = undefined;
	};
</script>

{#if useV2Components}
	<DoubleDeviceButtonV2
		id="doubledevicebutton-fic5qrl"
		aria-label={$format('label_direction', { default: 'Direction' })}
		isActive={btn1 || btn2}
		{isLoading}
		class={className}
		disabled={!hasRights}
		{...devBtnsV2Props.doubleDeviceButtonV2}
	>
		<DeviceButtonV2
			id="devicebutton-6etz4wr"
			slot="buttonLeft"
			icon={btn1 ? 'fa fa-pause' : 'fa fa-arrow-up'}
			isActive={false}
			aria-pressed={btn1}
			aria-label={$format('label_direction_up_or_pause', { default: 'Direction Up or Pause' })}
			on:click={(e) => {
				e.stopPropagation();
				handleRequest(1);
			}}
			{...devBtnsV2Props.deviceButtonV2}
		/>
		<DeviceButtonV2
			id="devicebutton-eve5fk1"
			slot="buttonRight"
			icon={btn2 ? 'fa fa-pause' : 'fa fa-arrow-down'}
			isActive={false}
			aria-pressed={btn2}
			aria-label={$format('label_direction_down_or_pause', { default: 'Direction Down or Pause' })}
			on:click={(e) => {
				e.stopPropagation();
				handleRequest(0);
			}}
			{...devBtnsV2Props.deviceButtonV2}
		/>
	</DoubleDeviceButtonV2>
{:else}
	<DoubleDeviceButton
		id="doubledevicebutton-fic5qrl"
		aria-label={$format('label_direction', { default: 'Direction' })}
		isActive={btn1 || btn2}
		{isLoading}
		class={className}
		disabled={!hasRights}
	>
		<DeviceButton
			id="devicebutton-6etz4wr"
			slot="buttonLeft"
			icon={btn1 ? 'fa fa-pause' : 'fa fa-arrow-up'}
			aria-pressed={btn1}
			aria-label={$format('label_direction_up_or_pause', { default: 'Direction Up or Pause' })}
			size="m"
			on:click={(e) => {
				e.stopPropagation();
				handleRequest(1);
			}}
		/>
		<DeviceButton
			id="devicebutton-eve5fk1"
			slot="buttonRight"
			icon={btn2 ? 'fa fa-pause' : 'fa fa-arrow-down'}
			aria-pressed={btn2}
			aria-label={$format('label_direction_down_or_pause', { default: 'Direction Down or Pause' })}
			size="m"
			on:click={(e) => {
				e.stopPropagation();
				handleRequest(0);
			}}
		/>
	</DoubleDeviceButton>
{/if}

{#if showModal}
	<PinUnlock bind:showModal on:unlock={handlePin} on:cancel={() => (showModal = false)} />
{/if}
